import useVisible from "../../hooks/useVisible.jsx";
import { getCachedScript } from "../../utils/scriptCache.js";

const LINEAR = 6612;
const BEZIER = 6613;
const EPS = 0.001;
const MIN_INFLUENCE = 0.1;
const LINEAR_ERROR = 0.0015;
const LINEAR_CV = 0.015;
const BAKE_SEGMENT_ERROR = 0.006;

function valueDimensions(value) {
  if (typeof value === "number") return 1;
  if (Array.isArray(value)) return value.length;
  return 0;
}

function getValue(value, dimension) {
  if (typeof value === "number") return value;
  return value[dimension];
}

function sign(value) {
  if (value > EPS) return 1;
  if (value < -EPS) return -1;
  return 0;
}

function hasIndex(indices, index) {
  return indices.indexOf(index) !== -1;
}

function addIndex(indices, index) {
  if (index < 0) return;

  if (!hasIndex(indices, index)) {
    indices.push(index);
    indices.sort((a, b) => a - b);
  }
}

function addSoftIndex(indices, index) {
  if (index < 0) return;

  for (let i = 0; i < indices.length; i++) {
    if (Math.abs(indices[i] - index) <= 1) return;
  }

  addIndex(indices, index);
}

function distance(a, b) {
  if (typeof a === "number") return Math.abs(a - b);

  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    const diff = a[i] - b[i];
    sum += diff * diff;
  }

  return Math.sqrt(sum);
}

function valueDelta(a, b, dimension, useDistance) {
  if (useDistance) return distance(a, b);
  return getValue(b, dimension) - getValue(a, dimension);
}

function detectKeyIndices(samples) {
  const indices = [0, samples.length - 1];
  const dimensions = valueDimensions(samples[0].value);
  const dimensionVelocities = [];
  const dimensionMaxVelocities = [];

  for (let d = 0; d < dimensions; d++) {
    let previousDeltaSign = 0;
    const velocities = [];
    let maxVelocity = 0;

    for (let i = 1; i < samples.length; i++) {
      const duration = samples[i].time - samples[i - 1].time;
      if (duration <= EPS) continue;

      const delta = getValue(samples[i].value, d) - getValue(samples[i - 1].value, d);
      const currentDeltaSign = sign(delta);
      const currentVelocity = Math.abs(delta / duration);

      velocities.push(currentVelocity);
      if (currentVelocity > maxVelocity) maxVelocity = currentVelocity;

      if (previousDeltaSign !== 0 && currentDeltaSign === 0) addIndex(indices, i - 1);
      if (previousDeltaSign === 0 && currentDeltaSign !== 0) addIndex(indices, i - 1);
      if (currentDeltaSign === 0) previousDeltaSign = 0;

      if (currentDeltaSign !== 0) {
        if (previousDeltaSign !== 0 && currentDeltaSign !== previousDeltaSign) {
          addIndex(indices, i - 1);
        }
        previousDeltaSign = currentDeltaSign;
      }
    }

    dimensionVelocities[d] = velocities;
    dimensionMaxVelocities[d] = maxVelocity;
  }

  for (let d = 0; d < dimensions; d++) {
    const velocities = dimensionVelocities[d];
    const maxVelocity = dimensionMaxVelocities[d];

    for (let i = 2; i < velocities.length - 2; i++) {
      const isValley =
        velocities[i] <= velocities[i - 1] + EPS &&
        velocities[i] <= velocities[i + 1] + EPS &&
        velocities[i] < maxVelocity * 0.1;
      const hasMotionAround =
        velocities[i - 2] > velocities[i] * 2 + EPS &&
        velocities[i + 2] > velocities[i] * 2 + EPS;

      if (isValley && hasMotionAround) {
        let valleyIndex = i;
        if (
          velocities[i + 1] <= velocities[i] + EPS &&
          velocities[i + 1] <= velocities[i + 2] + EPS
        ) {
          valleyIndex = i + 1;
        }
        addSoftIndex(indices, valleyIndex);
      }
    }
  }

  return indices;
}

function segmentAverageSpeed(samples, fromIndex, toIndex, dimension, useDistance) {
  const duration = samples[toIndex].time - samples[fromIndex].time;
  if (duration === 0) return 0;
  return Math.abs(valueDelta(samples[fromIndex].value, samples[toIndex].value, dimension, useDistance) / duration);
}

function normalizedSegmentValue(samples, sampleIndex, fromIndex, toIndex, dimension, useDistance) {
  const fromValue = samples[fromIndex].value;
  const toValue = samples[toIndex].value;
  const value = samples[sampleIndex].value;
  const delta = valueDelta(fromValue, toValue, dimension, useDistance);

  if (Math.abs(delta) <= EPS) return 0;
  if (useDistance) return distance(fromValue, value) / Math.abs(delta);
  return (getValue(value, dimension) - getValue(fromValue, dimension)) / delta;
}

function cubicAt(p0, p1, p2, p3, u) {
  const inv = 1 - u;
  return inv * inv * inv * p0 + 3 * inv * inv * u * p1 + 3 * inv * u * u * p2 + u * u * u * p3;
}

function cubicUnitBezierY(x, outInfluence, inInfluence, outY, inY) {
  let low = 0;
  let high = 1;
  let u = x;

  for (let i = 0; i < 18; i++) {
    const currentX = cubicAt(0, outInfluence, 1 - inInfluence, 1, u);
    if (currentX < x) low = u;
    else high = u;
    u = (low + high) / 2;
  }

  return cubicAt(0, outY, inY, 1, u);
}

function segmentBezierError(samples, fromIndex, toIndex, dimension, useDistance, outInfluence, inInfluence, outY, inY) {
  const duration = samples[toIndex].time - samples[fromIndex].time;
  let total = 0;
  let count = 0;

  for (let i = fromIndex + 1; i < toIndex; i++) {
    const x = (samples[i].time - samples[fromIndex].time) / duration;
    const y = normalizedSegmentValue(samples, i, fromIndex, toIndex, dimension, useDistance);
    const predicted = cubicUnitBezierY(x, outInfluence, inInfluence, outY, inY);
    const diff = predicted - y;
    total += diff * diff;
    count++;
  }

  return count === 0 ? 0 : Math.sqrt(total / count);
}

function fitBezierSegment(samples, fromIndex, toIndex, dimension, useDistance) {
  const averageSpeed = segmentAverageSpeed(samples, fromIndex, toIndex, dimension, useDistance);
  const delta = valueDelta(samples[fromIndex].value, samples[toIndex].value, dimension, useDistance);
  const duration = samples[toIndex].time - samples[fromIndex].time;
  let best = null;

  if (Math.abs(delta) <= EPS || toIndex - fromIndex <= 1 || averageSpeed <= EPS) {
    return { inSpeed: 0, outSpeed: 0, inInfluence: 16.6667, outInfluence: 16.6667, error: 0, linear: true };
  }

  const speedModes = [
    { outSpeed: 0, inSpeed: 0 },
    { outSpeed: averageSpeed, inSpeed: 0 },
    { outSpeed: 0, inSpeed: averageSpeed },
    { outSpeed: averageSpeed, inSpeed: averageSpeed },
  ];
  const influencePresets = [0.166667, 0.3, 0.333333, 0.5, 0.666667, 0.8, 1];

  function testCandidate(candidateOutSpeed, candidateInSpeed, candidateOutInfluence, candidateInInfluence) {
    const outY = (candidateOutSpeed * candidateOutInfluence * duration) / Math.abs(delta);
    const inY = 1 - (candidateInSpeed * candidateInInfluence * duration) / Math.abs(delta);
    const error = segmentBezierError(samples, fromIndex, toIndex, dimension, useDistance, candidateOutInfluence, candidateInInfluence, outY, inY);

    if (!best || error < best.error) {
      best = {
        outSpeed: candidateOutSpeed,
        inSpeed: candidateInSpeed,
        outInfluence: candidateOutInfluence,
        inInfluence: candidateInInfluence,
        outY,
        inY,
        error,
      };
    }
  }

  for (const mode of speedModes) {
    for (const outInfluence of influencePresets) {
      for (const inInfluence of influencePresets) {
        testCandidate(mode.outSpeed, mode.inSpeed, outInfluence, inInfluence);
      }
    }
  }

  if (best.error > BAKE_SEGMENT_ERROR) {
    for (const mode of speedModes) {
      for (let outInfluence = 0.05; outInfluence <= 1.0001; outInfluence += 0.05) {
        for (let inInfluence = 0.05; inInfluence <= 1.0001; inInfluence += 0.05) {
          testCandidate(mode.outSpeed, mode.inSpeed, outInfluence, inInfluence);
        }
      }
    }
  }

  let step = 0.025;
  while (step >= 0.005) {
    const baseOut = best.outInfluence;
    const baseIn = best.inInfluence;

    for (let oi = baseOut - step; oi <= baseOut + step + EPS; oi += step) {
      for (let ii = baseIn - step; ii <= baseIn + step + EPS; ii += step) {
        const outInfluence = Math.max(MIN_INFLUENCE / 100, Math.min(1, oi));
        const inInfluence = Math.max(MIN_INFLUENCE / 100, Math.min(1, ii));
        testCandidate(best.outSpeed, best.inSpeed, outInfluence, inInfluence);
      }
    }

    step /= 2;
  }

  const linearError = segmentBezierError(samples, fromIndex, toIndex, dimension, useDistance, 1 / 3, 1 / 3, 1 / 3, 2 / 3);

  return {
    inSpeed: best.inSpeed,
    outSpeed: best.outSpeed,
    inInfluence: Math.max(MIN_INFLUENCE, Math.min(100, best.inInfluence * 100)),
    outInfluence: Math.max(MIN_INFLUENCE, Math.min(100, best.outInfluence * 100)),
    error: best.error,
    linear: linearError <= LINEAR_ERROR && linearError <= best.error + LINEAR_ERROR,
  };
}

function segmentHasConstantVelocity(samples, fromIndex, toIndex, dimension, useDistance) {
  const velocities = [];
  let sum = 0;

  if (toIndex - fromIndex <= 1) return true;

  for (let i = fromIndex + 1; i <= toIndex; i++) {
    const duration = samples[i].time - samples[i - 1].time;
    if (duration <= EPS) continue;

    const velocity = Math.abs(valueDelta(samples[i - 1].value, samples[i].value, dimension, useDistance) / duration);
    velocities.push(velocity);
    sum += velocity;
  }

  if (velocities.length <= 1) return true;
  const mean = sum / velocities.length;
  if (mean <= EPS) return true;

  let variance = 0;
  for (const velocity of velocities) {
    const diff = velocity - mean;
    variance += diff * diff;
  }

  return Math.sqrt(variance / velocities.length) / mean < LINEAR_CV;
}

function segmentIsLinear(samples, fromIndex, toIndex, easeCount, useDistance) {
  let hasMotion = false;

  for (let d = 0; d < easeCount; d++) {
    const dimension = useDistance ? 0 : d;
    const delta = valueDelta(samples[fromIndex].value, samples[toIndex].value, dimension, useDistance);

    if (Math.abs(delta) > EPS) {
      hasMotion = true;
      if (!segmentHasConstantVelocity(samples, fromIndex, toIndex, dimension, useDistance)) return false;
    }
  }

  return hasMotion;
}

function buildSegmentFits(samples, indices, easeCount, useDistance) {
  const fits = [];

  function cloneFit(fit) {
    return {
      inSpeed: fit.inSpeed,
      outSpeed: fit.outSpeed,
      inInfluence: fit.inInfluence,
      outInfluence: fit.outInfluence,
      error: fit.error,
      linear: fit.linear,
      delta: fit.delta || 0,
    };
  }

  for (let s = 0; s < indices.length - 1; s++) {
    const isLinear = segmentIsLinear(samples, indices[s], indices[s + 1], easeCount, useDistance);
    let best = null;
    const dimensionFits = [];

    if (isLinear) {
      fits.push({ inSpeed: 0, outSpeed: 0, inInfluence: 16.6667, outInfluence: 16.6667, error: 0, linear: true, delta: 0, dimensionFits: [] });
      continue;
    }

    for (let d = 0; d < easeCount; d++) {
      const dimension = useDistance ? 0 : d;
      const delta = Math.abs(valueDelta(samples[indices[s]].value, samples[indices[s + 1]].value, dimension, useDistance));
      if (delta <= EPS) continue;

      const fit = fitBezierSegment(samples, indices[s], indices[s + 1], dimension, useDistance);
      fit.delta = delta;
      dimensionFits[d] = fit;
      if (!best || fit.error < best.error || delta > best.delta) best = fit;
    }

    if (!best) best = { inSpeed: 0, outSpeed: 0, inInfluence: 16.6667, outInfluence: 16.6667, error: 0, linear: true, delta: 0 };
    const segmentFit = cloneFit(best);
    segmentFit.linear = false;
    segmentFit.dimensionFits = dimensionFits.map((fit) => fit ? cloneFit(fit) : fit);
    fits.push(segmentFit);
  }

  return fits;
}

function segmentFitError(samples, fromIndex, toIndex, easeCount, useDistance) {
  let maxError = 0;
  let hasMotion = false;

  for (let d = 0; d < easeCount; d++) {
    const dimension = useDistance ? 0 : d;
    const delta = Math.abs(valueDelta(samples[fromIndex].value, samples[toIndex].value, dimension, useDistance));
    if (delta <= EPS) continue;

    const fit = fitBezierSegment(samples, fromIndex, toIndex, dimension, useDistance);
    hasMotion = true;
    if (fit.error > maxError) maxError = fit.error;
  }

  return hasMotion ? maxError : 0;
}

function segmentHasOvershoot(samples, fromIndex, toIndex) {
  const dimensions = valueDimensions(samples[0].value);

  for (let d = 0; d < dimensions; d++) {
    const startValue = getValue(samples[fromIndex].value, d);
    const endValue = getValue(samples[toIndex].value, d);
    const minValue = Math.min(startValue, endValue);
    const maxValue = Math.max(startValue, endValue);
    const range = Math.max(EPS, maxValue - minValue);
    const margin = Math.max(EPS, range * 0.002);

    for (let i = fromIndex + 1; i < toIndex; i++) {
      const value = getValue(samples[i].value, d);
      if (value < minValue - margin || value > maxValue + margin) return true;
    }
  }

  return false;
}

function addBakeFramesWhereCurveFails(samples, indices, easeCount, useDistance) {
  const refined = indices.slice(0);

  for (let s = 0; s < indices.length - 1; s++) {
    const fromIndex = indices[s];
    const toIndex = indices[s + 1];
    let failed = false;

    if (segmentHasOvershoot(samples, fromIndex, toIndex)) failed = true;
    else if (!segmentIsLinear(samples, fromIndex, toIndex, easeCount, useDistance)) {
      failed = segmentFitError(samples, fromIndex, toIndex, easeCount, useDistance) > BAKE_SEGMENT_ERROR;
    }

    if (failed) {
      for (let i = fromIndex + 1; i < toIndex; i++) {
        if (!hasIndex(refined, i)) refined.push(i);
      }
    }
  }

  refined.sort((a, b) => a - b);
  return refined;
}

function zeroVector(value) {
  return value.map(() => 0);
}

function straightSpatialTangents(fromValue, toValue) {
  const outTangent = [];
  const inTangent = [];

  for (let i = 0; i < fromValue.length; i++) {
    const third = (toValue[i] - fromValue[i]) / 3;
    outTangent.push(third);
    inTangent.push(-third);
  }

  return { outTangent, inTangent };
}

function fitSpatialSegment(samples, fromIndex, toIndex) {
  const fromValue = samples[fromIndex].value;
  const toValue = samples[toIndex].value;
  const dimensions = fromValue.length;
  const straight = straightSpatialTangents(fromValue, toValue);

  if (toIndex - fromIndex <= 1) return straight;

  let c11 = 0;
  let c12 = 0;
  let c22 = 0;
  const r1 = Array(dimensions).fill(0);
  const r2 = Array(dimensions).fill(0);
  let totalLength = 0;
  const lengths = [];

  lengths[fromIndex] = 0;
  for (let i = fromIndex + 1; i <= toIndex; i++) {
    totalLength += distance(samples[i - 1].value, samples[i].value);
    lengths[i] = totalLength;
  }

  if (totalLength <= EPS) return straight;

  for (let i = fromIndex + 1; i < toIndex; i++) {
    const u = Math.max(0, Math.min(1, lengths[i] / totalLength));
    const inv = 1 - u;
    const b0 = inv * inv * inv;
    const b1 = 3 * inv * inv * u;
    const b2 = 3 * inv * u * u;
    const b3 = u * u * u;

    c11 += b1 * b1;
    c12 += b1 * b2;
    c22 += b2 * b2;

    for (let d = 0; d < dimensions; d++) {
      const target = samples[i].value[d] - b0 * fromValue[d] - b3 * toValue[d];
      r1[d] += b1 * target;
      r2[d] += b2 * target;
    }
  }

  const det = c11 * c22 - c12 * c12;
  if (Math.abs(det) <= EPS) return straight;

  const outTangent = [];
  const inTangent = [];

  for (let d = 0; d < dimensions; d++) {
    const p1 = (r1[d] * c22 - r2[d] * c12) / det;
    const p2 = (c11 * r2[d] - c12 * r1[d]) / det;
    outTangent.push(p1 - fromValue[d]);
    inTangent.push(p2 - toValue[d]);
  }

  return { outTangent, inTangent };
}

function buildSpatialTangents(samples, indices, isSpatial) {
  if (!isSpatial || valueDimensions(samples[0].value) <= 1) return null;

  const tangents = indices.map((index) => ({
    inTangent: zeroVector(samples[index].value),
    outTangent: zeroVector(samples[index].value),
  }));

  for (let s = 0; s < indices.length - 1; s++) {
    const segmentTangents = fitSpatialSegment(samples, indices[s], indices[s + 1]);
    tangents[s].outTangent = segmentTangents.outTangent;
    tangents[s + 1].inTangent = segmentTangents.inTangent;
  }

  return tangents;
}

function processProperty(property) {
  const samples = property.samples || [];
  if (samples.length === 0) return property;

  const dimensions = valueDimensions(samples[0].value);
  if (dimensions === 0) {
    return {
      ...property,
      times: samples.map((sample) => sample.time),
      values: samples.map((sample) => sample.value),
      segmentFits: null,
      spatialTangents: null,
    };
  }

  let easeCount = property.easeCount || dimensions;
  let useDistance = false;

  if (property.isSpatial && dimensions > 1) {
    easeCount = 1;
    useDistance = true;
  }

  if (easeCount === 1 && dimensions > 1) useDistance = true;

  let indices = detectKeyIndices(samples);
  indices = addBakeFramesWhereCurveFails(samples, indices, easeCount, useDistance);

  return {
    ...property,
    indices,
    segmentFits: buildSegmentFits(samples, indices, easeCount, useDistance),
    spatialTangents: buildSpatialTangents(samples, indices, property.isSpatial),
  };
}

function processTrueBakeData(data) {
  return {
    ...data,
    properties: (data.properties || []).map(processProperty),
  };
}

function evalScript(script) {
  return new Promise((resolve) => {
    window.jsx.evalScript(script, resolve);
  });
}

function runAeFunction(functionName, argument) {
  return evalScript(`run(${functionName}(${JSON.stringify(argument)}));`);
}

function readCepJsonFile(filePath) {
  const result = window.cep.fs.readFile(filePath);

  if (result.err !== 0) {
    throw new Error(`Cannot read file ${filePath}: ${result.err}`);
  }

  return JSON.parse(result.data);
}

function writeCepJsonFile(filePath, data) {
  const result = window.cep.fs.writeFile(
    filePath,
    JSON.stringify(data),
    window.cep.encoding.UTF8,
  );

  if (result.err !== 0) {
    throw new Error(`Cannot write file ${filePath}: ${result.err}`);
  }
}

function resultFilePath(dataFilePath) {
  return dataFilePath.replace(/true_bake_data\.json$/i, "true_bake_result.json");
}

async function runTrueBake(argument) {
  try {
    if (!window.jsx) return false;
    getCachedScript();
    const totalStart = Date.now();

    if (argument.type === "value" || argument.expressionError) {
      await runAeFunction("trueBakeExpression", argument);
      return false;
    }

    const dataPath = await runAeFunction("trueBakeExpressionDataToFile", argument);
    if (!dataPath || dataPath === "undefined" || dataPath === "EvalScript error.") {
      throw new Error(`trueBakeExpressionDataToFile returned: ${dataPath}`);
    }

    const bakeData = readCepJsonFile(dataPath);
    if (!bakeData.ok) {
      throw new Error(bakeData.error || "AE returned empty true bake data");
    }

    const mathStart = Date.now();
    const processedData = processTrueBakeData(bakeData);
    const mathTimeMs = Date.now() - mathStart;

    const resultPath = resultFilePath(dataPath);
    writeCepJsonFile(resultPath, processedData);

    const applyRawData = await runAeFunction("trueBakeExpressionApplyFromFile", resultPath);
    if (!applyRawData || applyRawData === "undefined" || applyRawData === "EvalScript error.") {
      throw new Error(`trueBakeExpressionApplyFromFile returned: ${applyRawData}`);
    }

    if (argument.notification) {
      JSON.parse(applyRawData);
      const totalTimeInSeconds = Math.round((Date.now() - totalStart) / 1000);
      const minutes = Math.floor(totalTimeInSeconds / 60);
      const seconds = totalTimeInSeconds % 60;
      const message = `Calculate Time: ${minutes} min ${seconds} sec`;
      await evalScript(`run($.ae.message(${JSON.stringify(message)}));`);
    }
  } catch (error) {
    const message = error && error.message ? error.message : String(error);
    console.error("TrueBakeExpression CEP error", error);
    await evalScript(`run($.ae.message(${JSON.stringify(`True Bake CEP error: ${message}`)}));`);
  }

  return false;
}

function TrueBakeExpressionGroup({
  selectedTag,
  buttonTags,
  generateButtonTags,
  button,
}) {
  const [isVisible, setIsVisible] = useVisible("TrueBakeExpressionGroupVisibility");
  const [bakeColor, setBakeColor] = useVisible("trueBakeColor");
  const [bakeTimeRemap, setBakeTimeRemap] = useVisible("TrueBakeExpressionTimeRemap");

  if (selectedTag && !buttonTags.includes(selectedTag)) {
    return null;
  }

  return (
    <>
      <li
        onClick={() => setIsVisible(!isVisible)}
        className={`button-card resolution-toggle ${isVisible ? "active" : ""}`}
      >
        <div className="button-card__icon">
          <div dangerouslySetInnerHTML={{ __html: button.icon }} />
          <div />
        </div>
        <div className="button-card__title">{button.name}</div>
        {generateButtonTags()}
      </li>
      <div className={`render-group ${isVisible ? "" : "render-group--hidden"}`}>
        <div className="render-group__checkbox-group">
          <div className="render-group__checkbox">
            <input
              type="checkbox"
              id="trueBakeColor"
              checked={bakeColor}
              onChange={() => setBakeColor(!bakeColor)}
              className="hidden-checkbox"
            />
            <label htmlFor="trueBakeColor">
              <span className="custom-checkbox"></span>
              <span className="label-text">Bake Color</span>
            </label>
          </div>
          <div className="render-group__checkbox">
            <input
              type="checkbox"
              id="trueBakeTimeRemap"
              checked={bakeTimeRemap}
              onChange={() => setBakeTimeRemap(!bakeTimeRemap)}
              className="hidden-checkbox"
            />
            <label htmlFor="trueBakeTimeRemap">
              <span className="custom-checkbox"></span>
              <span className="label-text">Bake TimeRemap</span>
            </label>
          </div>
        </div>
        <div className="videobolt-tools">
          <div onClick={() => runTrueBake({ type: "property", notification: true })} className="resolution-tools__button">
            <i className="fa-solid fa-arrow-pointer"></i>
            <span>Selected Properties</span>
          </div>
          <div onClick={() => runTrueBake({ type: "layer", bakeColor, bakeTimeRemap, notification: true })} className="resolution-tools__button">
            <i className="fa-light fa-layer-group"></i>
            <span>Selected Layers</span>
          </div>
          <div onClick={() => runTrueBake({ type: "comp", bakeColor, bakeTimeRemap, notification: true })} className="resolution-tools__button">
            <i className="fa-solid fa-image-polaroid"></i>
            <span>Active Comp</span>
          </div>
          <div onClick={() => runTrueBake({ type: "selected comp", bakeColor, bakeTimeRemap, notification: true })} className="resolution-tools__button">
            <i className="fa-solid fa-images"></i>
            <span>Selected Comps</span>
          </div>
          <div onClick={() => runTrueBake({ type: "project", bakeColor, bakeTimeRemap, notification: true })} className="resolution-tools__button">
            <i className="fa-regular fa-photo-film"></i>
            <span>Project</span>
          </div>
          <div onClick={() => runTrueBake({ type: "value", bakeColor, bakeTimeRemap, notification: true })} className="resolution-tools__button">
            <i className="fa-regular fa-trash"></i>
            <span>Delete Expression</span>
          </div>
          <div onClick={() => runTrueBake({ type: "project", bakeColor, bakeTimeRemap, expressionError: true, notification: true })} className="resolution-tools__button">
            <i className="fa-regular fa-triangle-exclamation"></i>
            <span>Delete Error</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default TrueBakeExpressionGroup;
